import React, { useEffect } from "react";
import { OptionType, IDType } from "../../@lib/types";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Controller } from "react-hook-form";
import { ReactComponent as UploadCloudIcon } from "../../icons/upload-cloud-icon.svg";

import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Attachment } from "../../features";
import { DatePicker, useLanguageTranslator } from "../../components";
import dayjs from "dayjs";

export type PracticeInfoFormProps = {
  countryOptions: OptionType<IDType>[];
  themeOptions: OptionType<IDType>[];
  categoryOptions: (OptionType<IDType> & {
    practiceTypeOptions: OptionType<IDType>[];
  })[];
  disabled?: boolean;
  setImageAttachmentDetails: any;
  imageAttachmentDetails: any;
  documentAttachmentDetails: any;
  setDocumentAttachmentDetails: any;
  defaultPracticeCategoryId?: any;
  data?: any;
  practiceTypeOptions?: any;
};

const PracticeInfoForm: React.FC<PracticeInfoFormProps> = (props) => {
  const {
    countryOptions,
    themeOptions,
    categoryOptions,
    disabled,
    setImageAttachmentDetails,
    documentAttachmentDetails,
    imageAttachmentDetails,
    setDocumentAttachmentDetails,
    data,
    practiceTypeOptions,
  } = props;

  const { trigger, setValue, getValues } = useFormContext();
  const [imageAttachmentObject, setImageAttachmentObject] = useState<any>({});
  const [documentsAttachmentObject, setDocumentsAttachmentObject] =
    useState<any>({});

  const { translator } = useLanguageTranslator();

  useEffect(() => {
    if (data?.practice?.picture?.originalFilename) {
      setImageAttachmentObject({
        name: data?.practice?.picture?.originalFilename,
      });
    }
    if (data?.practice?.document?.originalFilename) {
      setDocumentsAttachmentObject({
        name: data?.practice?.document?.originalFilename,
      });
    }
  }, [data?.practice]);

  return (
    <Stack direction={{ xs: "column-reverse", md: "row" }} gap={3}>
      <Stack flex={1} gap={2}>
        <FormControl fullWidth>
          <FormLabel>{translator("Upload Image")}</FormLabel>
          <Attachment
            allowedFormats={["svg", "png", "jpg", "gif", "jpeg"]}
            text="SVG, PNG, JPG or GIF (max. 1 MB)"
            setAttachmentDetails={setImageAttachmentDetails}
            attachmentDetails={imageAttachmentDetails}
            attachmentObject={imageAttachmentObject}
            setAttachmentObject={setImageAttachmentObject}
            variant={{ variant: "outlined", py: 10 }}
          />
        </FormControl>

        <Controller
          name="countryId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth required error={!!error} disabled={disabled}>
              <FormLabel>{translator("Country")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>
                    {translator("support.Select")} {translator("Country")}
                  </em>
                </MenuItem>
                {countryOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {error?.message && translator(error.message)}
              </FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="themeId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth required error={!!error} disabled={disabled}>
              <FormLabel>{translator("Theme")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>
                    {" "}
                    {translator("support.Select")} {translator("Theme")}
                  </em>
                </MenuItem>
                {themeOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {error?.message && translator(error.message)}
              </FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="practiceCategoryId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth required error={!!error} disabled={disabled}>
              <FormLabel>{translator("dashboardTableHead.Category")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue("practiceTypeId", "", { shouldValidate: true });
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>
                    {translator("support.Select")}{" "}
                    {translator("dashboardTableHead.Category")}
                  </em>
                </MenuItem>
                {categoryOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {error?.message && translator(error.message)}
              </FormHelperText>
            </FormControl>
          )}
        />

        {practiceTypeOptions.length > 0 && (
          <Controller
            name="practiceTypeId"
            rules={{ required: "Required!" }}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                required
                fullWidth
                error={!!error}
                disabled={disabled}
              >
                <FormLabel>{translator("Type")}</FormLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger(field.name);
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>
                      {translator("support.Select")} {translator("Type")}
                    </em>
                  </MenuItem>
                  {practiceTypeOptions.map((op: any, i: any) => (
                    <MenuItem key={`${op.value}-${i}`} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                {error?.message && translator(error.message)}
              </FormHelperText>
              </FormControl>
            )}
          />
        )}
      </Stack>

      <Stack flex={1} gap={2}>
        <Controller
          name="title"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl required error={!!error} disabled={disabled}>
              <FormLabel>
                {translator("Practices")?.slice(0, -1)}{" "}
                {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
              />
              <FormHelperText>
                {error?.message && translator(error.message)}
              </FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="description"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>
                {translator("Practices")?.slice(0, -1)}{" "}
                {translator("dashboardTableHead.Description")}
              </FormLabel>
              <OutlinedInput {...field} multiline rows={3} />
            </FormControl>
          )}
        />

        <Controller
          name="date"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>
                {translator("dashboardTableHead.Date Created")}
              </FormLabel>
              {/* <OutlinedInput {...field} /> */}
              <DatePicker
                selected={field?.value}
                onSelect={(date: any) => {
                  if (date) {
                    const formattedDate = dayjs(date).format("YYYY-MM-DD");
                    field.onChange(formattedDate);
                  }
                }}
              />
            </FormControl>
          )}
        />

        <FormControl fullWidth>
          <FormLabel>{translator("Upload document")}</FormLabel>
          <Attachment
            allowedFormats={["pdf"]}
            text="PDF file (max. 50mb)"
            setAttachmentDetails={setDocumentAttachmentDetails}
            attachmentDetails={documentAttachmentDetails}
            attachmentObject={documentsAttachmentObject}
            setAttachmentObject={setDocumentsAttachmentObject}
            variant={{ variant: "outlined" }}
          />
        </FormControl>

        <Controller
          name="documentTitle"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>{translator(`Document Title`)}</FormLabel>
              <OutlinedInput {...field} />
            </FormControl>
          )}
        />

        <Controller
          name="link"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>{translator("External Practice Link")}</FormLabel>
              <OutlinedInput {...field} />
            </FormControl>
          )}
        />
      </Stack>
    </Stack>
  );
};

export default PracticeInfoForm;
